import { ExpandMore as ExpandMoreIcon } from 'components/icons';
import InfoTooltip from 'components/InfoTooltip';
import AuditInfo from 'components/audit/AuditInfo';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import { useInvoice } from 'pages/invoices/hooks/useInvoice';
import { useInvoiceFlow } from 'pages/invoices/hooks/useInvoiceFlow';
import { InvoiceTransitionAlertMessage } from 'pages/invoices/util/invoiceUtil';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { ClassifierData } from 'types/classifiers';
import { InvoiceData, PrepaymentDataListItem } from 'types/invoices';
import { cn } from 'utils/cn';
import { useHashScroll } from 'utils/useHashScroll';
import InvoiceLoader from './components/InvoiceLoader';
import { DetailsAlert } from './details-tab/Details';
import { SECTION_BODY, SECTION_CARD, SECTION_HEADER, SECTION_PANEL, SECTION_TITLE } from './details-tab/SectionCard';
import DebtCollectionCard from './general-tab/DebtCollectionCard';
import DetailsCard from './general-tab/DetailsCard';
import InvoiceDetailsCard from './general-tab/InvoiceDetailsCard';
import KsefDetailsCard from './general-tab/KsefDetailsCard';
import PrepaymentUsageCard from './general-tab/PrepaymentUsageCard';
import PreviousInvoicesCard from './general-tab/PreviousInvoicesCard';
import RecipientsCard from './general-tab/RecipientsCard';
import RevenueTeamCard from './general-tab/RevenueTeamCard';
import ExistingPaymentsCard from './general-tab/existing-payments/ExistingPaymentsCard';
import { useShowsExistingPayments, useShowsKsefDetails } from './general-tab/useGeneralCardRules';

/**
 * One card of the tab. The whole title row toggles the body, which stays mounted while closed so its
 * fields keep registering with the form and its queries keep running, and animates its height.
 */
function GeneralSection({
    titleId,
    defaultOpen = false,
    titleAdornment,
    bodyClassName,
    children,
}: {
    titleId: string;
    defaultOpen?: boolean;
    titleAdornment?: ReactNode;
    bodyClassName?: string;
    children: ReactNode;
}) {
    return (
        <Collapsible defaultOpen={defaultOpen} className={SECTION_CARD}>
            <CollapsibleTrigger
                className={cn(
                    SECTION_HEADER,
                    'group focus-visible:ring-ring/50 w-full cursor-default rounded-md text-left outline-none focus-visible:ring-3'
                )}
            >
                <span className={cn(SECTION_TITLE, 'my-2.5 ml-2 flex flex-1 items-center')}>
                    <FormattedMessage id={titleId} />
                    {titleAdornment}
                </span>
                <ExpandMoreIcon className="text-grey-500 cursor-pointer transition-transform group-data-[panel-open]:rotate-90" />
            </CollapsibleTrigger>
            <CollapsibleContent keepMounted className={SECTION_PANEL}>
                <div className={cn(SECTION_BODY, bodyClassName)}>{children}</div>
            </CollapsibleContent>
        </Collapsible>
    );
}

type Props = {
    invoice: InvoiceData;
    classifiers: Record<string, ClassifierData>;
    isInViewMode: boolean;
    handleStatusLoadingState: (isLoading: boolean) => void;
    isLoading: boolean;
    searchExistingPrepaymentsText: string;
    setSearchExistingPrepaymentsText: (value: string) => void;
    submitInvoice: () => void;
    submitTemplateUpdate: () => void;
    isTemplateEditable: boolean;
    updateInvoiceLimited: () => void;
    existingPrepayments?: PrepaymentDataListItem[];
};

const General = ({
    invoice,
    classifiers,
    isInViewMode,
    isLoading,
    handleStatusLoadingState,
    searchExistingPrepaymentsText,
    setSearchExistingPrepaymentsText,
    submitInvoice,
    submitTemplateUpdate,
    isTemplateEditable,
    updateInvoiceLimited,
    existingPrepayments,
}: Props) => {
    const { isCredit, isFinalized, isPrepayment } = useInvoice(invoice);
    const showExistingPayments = useShowsExistingPayments(invoice);

    const {
        isRequestedToPostpone,
        isPostponeDisagreedContinued,
        isPostponeRejected,
        isInvoiceRejected,
        isPostponeConfirmed,
    } = useInvoiceFlow(invoice);

    const showKsefDetails = useShowsKsefDetails(invoice);

    const lastTransition = invoice.invoiceTransitions[0];
    const alertMessageIds = lastTransition
        ? [
              isRequestedToPostpone && 'invoices.alerts.requests-postpone-name',
              isPostponeDisagreedContinued && 'invoices.alerts.disagreed-continue-postpone-name',
              isPostponeRejected && 'invoices.alerts.rejected-postpone-name',
              isPostponeConfirmed && 'invoices.alerts.confirmed-postpone-name',
              isInvoiceRejected && !isFinalized && 'invoices.alerts.rejected-invoice-name',
          ].filter((id): id is string => !!id)
        : [];

    useHashScroll();

    return (
        <>
            {alertMessageIds.length ? (
                <div className="flex flex-col gap-2">
                    {alertMessageIds.map((messageId) => (
                        <DetailsAlert key={messageId} tone="warning">
                            <InvoiceTransitionAlertMessage messageId={messageId} lastTransition={lastTransition} />
                        </DetailsAlert>
                    ))}
                </div>
            ) : null}
            <GeneralSection titleId="invoices.general-tab.details.title" defaultOpen>
                {isLoading ? (
                    <InvoiceLoader />
                ) : (
                    <DetailsCard
                        invoice={invoice}
                        classifiers={classifiers}
                        isInViewMode={isInViewMode}
                        submitInvoice={submitInvoice}
                        submitTemplateUpdate={submitTemplateUpdate}
                        isTemplateEditable={isTemplateEditable}
                        updateInvoiceLimited={updateInvoiceLimited}
                        existingPrepayments={existingPrepayments}
                    />
                )}
            </GeneralSection>
            {showKsefDetails && (
                <GeneralSection titleId="invoices.general-tab.ksef-details.title" defaultOpen>
                    {isLoading ? (
                        <InvoiceLoader />
                    ) : (
                        <KsefDetailsCard
                            invoiceExternalEvents={invoice.invoiceExternalEvents ?? []}
                            externalStatusTypeCode={invoice.externalStatusTypeCode}
                            externalPayload={invoice.externalPayload}
                        />
                    )}
                </GeneralSection>
            )}
            <GeneralSection
                titleId="invoices.general-tab.previous-invoices.title"
                titleAdornment={
                    <InfoTooltip text="invoices.general-tab.previous-invoices.tooltips.title" className="ml-2" />
                }
            >
                {isLoading ? <InvoiceLoader /> : <PreviousInvoicesCard invoice={invoice} classifiers={classifiers} />}
            </GeneralSection>
            <GeneralSection titleId="invoices.general-tab.recipients.title" defaultOpen>
                {isLoading ? (
                    <InvoiceLoader />
                ) : (
                    <RecipientsCard
                        invoice={invoice}
                        isInViewMode={isInViewMode}
                        clientParty={invoice.clientParty}
                        updateInvoiceLimited={updateInvoiceLimited}
                    />
                )}
            </GeneralSection>
            {showExistingPayments && (
                <GeneralSection titleId="invoices.general-tab.existing-payments.title" defaultOpen>
                    {isLoading ? (
                        <InvoiceLoader />
                    ) : (
                        <ExistingPaymentsCard
                            invoice={invoice}
                            isInViewMode={isInViewMode || invoice.hasCreditInvoice}
                            searchText={searchExistingPrepaymentsText}
                            setSearchText={setSearchExistingPrepaymentsText}
                            existingPrepayments={existingPrepayments}
                        />
                    )}
                </GeneralSection>
            )}
            <GeneralSection titleId="invoices.general-tab.invoice-details.title" defaultOpen>
                {isLoading ? (
                    <InvoiceLoader />
                ) : (
                    <InvoiceDetailsCard
                        invoice={invoice}
                        classifiers={classifiers}
                        isInViewMode={isInViewMode}
                        submitInvoice={submitInvoice}
                        existingPrepayments={existingPrepayments}
                    />
                )}
            </GeneralSection>
            <GeneralSection
                titleId="invoices.debt-collection"
                defaultOpen
                bodyClassName="pt-4 @max-[1186px]/invoice:pt-4"
            >
                {isLoading ? <InvoiceLoader /> : <DebtCollectionCard invoice={invoice} />}
            </GeneralSection>
            {(!isCredit || !!invoice.invoicePayments?.length) && (
                <GeneralSection
                    titleId="invoices.general-tab.revenue-team.title"
                    defaultOpen
                    bodyClassName="pt-0 @max-[1186px]/invoice:pt-0"
                >
                    {isLoading ? (
                        <InvoiceLoader />
                    ) : (
                        <RevenueTeamCard
                            invoice={invoice}
                            classifiers={classifiers}
                            isInViewMode={isInViewMode}
                            handleStatusLoadingState={handleStatusLoadingState}
                        />
                    )}
                </GeneralSection>
            )}
            {isPrepayment && (
                <GeneralSection
                    titleId="invoices.general-tab.prepayment-usage.title"
                    defaultOpen
                    bodyClassName="pt-0 @max-[1186px]/invoice:pt-0"
                >
                    {isLoading ? <InvoiceLoader /> : <PrepaymentUsageCard invoice={invoice} />}
                </GeneralSection>
            )}
            <div>
                <AuditInfo
                    createdAt={invoice.createdAt}
                    createdByEmployee={invoice.createdByEmployee || undefined}
                    lastModifiedAt={invoice.lastModifiedAt}
                    changedByEmployee={invoice.changedByEmployee || undefined}
                />
            </div>
        </>
    );
};
export default General;
